<template>
  <v-menu
    v-model="menuOpen"
    :close-on-content-click="false"
    offset-y
    top
    nudge-top="8"
    max-width="380"
    min-width="320"
  >
    <template v-slot:activator="{ on, attrs }">
      <slot name="activator" :on="on" :attrs="attrs">
        <v-btn icon v-bind="attrs" v-on="on" title="Saved Replies">
          <v-icon>mdi-flash</v-icon>
        </v-btn>
      </slot>
    </template>

    <v-card class="saved-reply-card">
      <!-- Header + Search -->
      <div class="pa-2">
        <div class="d-flex align-center mb-2">
          <v-icon size="18" color="amber darken-2" class="mr-1">mdi-flash</v-icon>
          <span class="font-weight-medium">Saved Replies</span>
          <v-spacer />
          <v-btn icon x-small @click="reload" :loading="loading" :title="use_language.reload_again">
            <v-icon size="16">mdi-refresh</v-icon>
          </v-btn>
        </div>
        <v-text-field
          v-model="search"
          outlined dense hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          :placeholder="use_language.search + '...'"
        />
      </div>
      <v-divider />

      <!-- List -->
      <div class="reply-list">
        <div v-if="loading && replies.length === 0" class="py-4 text-center">
          <v-progress-circular indeterminate color="primary" size="20" />
        </div>

        <div
          v-else-if="filteredReplies.length === 0"
          class="py-4 text-center caption"
          style="color: grey"
        >
          {{ search ? use_language.no_matching_replies : use_language.no_replies_yet }}
        </div>

        <v-list dense class="py-0">
          <v-list-item
            v-for="r in filteredReplies"
            :key="r.id"
            @click="onPick(r)"
          >
            <v-list-item-content>
              <v-list-item-title class="font-weight-medium">{{ r.title }}</v-list-item-title>
              <v-list-item-subtitle class="text-truncate" style="color: #888">
                {{ r.content }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="my-0">
              <div class="d-flex">
                <v-btn icon x-small @click.stop="openEdit(r)" :title="use_language.edit">
                  <v-icon size="16">mdi-pencil-outline</v-icon>
                </v-btn>
                <v-btn icon x-small @click.stop="onDelete(r)" :title="use_language.delete">
                  <v-icon size="16" color="error">mdi-delete-outline</v-icon>
                </v-btn>
              </div>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </div>

      <v-divider />
      <div class="pa-2">
        <v-btn block small color="primary" @click="openCreate">
          <v-icon size="16" left>mdi-plus</v-icon>{{ use_language.create_new_reply }}
        </v-btn>
      </div>
    </v-card>

    <!-- Dialog: create / edit -->
    <v-dialog v-model="editorOpen" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h6">
          {{ editing ? use_language.edit + " Saved Reply" : use_language.create + " Saved Reply" }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="formTitle"
            :label="use_language.name + ' Replies *'"
            outlined dense
            counter="100"
            maxlength="100"
            class="mb-2"
          />
          <v-textarea
            v-model="formContent"
            :label="use_language.message + ' *'"
            outlined dense
            rows="5"
            auto-grow
            :placeholder="use_language.message_to_insert_into_send_box"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeEditor" :disabled="saving">{{ use_language.cancel }}</v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="!formTitle.trim() || !formContent.trim()"
            @click="onSubmit"
          >
            {{ editing ? use_language.button_save : use_language.create }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-menu>
</template>

<script>
import axios from "axios";
import { expressService_dotnet } from "@/website/global";
import { token_api } from "@/website/backend/token";
import { get_languages } from '@/website/global_function'

export default {
  name: "SavedReplyMenu",
  props: {
    shopId: { type: [String, Number], required: true },
    staffShopId: { type: [String, Number], default: null },
  },
  data: () => ({
    use_language: {},
    menuOpen: false,
    replies: [],
    loading: false,
    search: "",
    // editor dialog
    editorOpen: false,
    editing: null,         // null = create, object = edit
    formTitle: "",
    formContent: "",
    saving: false,
  }),
  computed: {
    filteredReplies() {
      const k = (this.search || "").toLowerCase().trim();
      if (!k) return this.replies;
      return this.replies.filter(
        (r) =>
          (r.title || "").toLowerCase().includes(k) ||
          (r.content || "").toLowerCase().includes(k)
      );
    },
    _headers() {
      return { "Content-Type": "application/json", Authorization: "Bearer " + token_api };
    },
  },
  watch: {
    // โหลดทุกครั้งที่เปิด menu — เผื่อ staff คนอื่นเพิ่ง create/edit
    menuOpen(val) {
      if (val && this.replies.length === 0) this.reload();
    },
  },
  async created() {
    this.use_language = await this.get_languages(localStorage.getItem("set_language"));
  },
  methods: {
    get_languages,
    onPick(r) {
      this.$emit("pick", r.content);
      this.menuOpen = false;
    },
    async reload() {
      this.loading = true;
      try {
        const res = await axios.post(
          expressService_dotnet + "ChatSavedReply/get-list",
          { shopId: this.shopId },
          { headers: this._headers }
        );
        this.replies = (res.data && res.data.replies) || [];
      } catch (e) {
        console.warn("[SavedReply] reload error:", e.message || e);
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.editing = null;
      this.formTitle = "";
      this.formContent = "";
      this.editorOpen = true;
    },
    openEdit(r) {
      this.editing = r;
      this.formTitle = r.title || "";
      this.formContent = r.content || "";
      this.editorOpen = true;
    },
    closeEditor() {
      this.editorOpen = false;
      this.editing = null;
    },
    async onSubmit() {
      const title = this.formTitle.trim();
      const content = this.formContent.trim();
      if (!title || !content) return;
      this.saving = true;
      try {
        if (this.editing) {
          // update
          const res = await axios.post(
            expressService_dotnet + "ChatSavedReply/update",
            {
              id: this.editing.id,
              shopId: this.shopId,
              title,
              content,
              staffShopId: this.staffShopId,
            },
            { headers: this._headers }
          );
          if (res.data && res.data.status === "success") {
            const target = this.replies.find((r) => r.id === this.editing.id);
            if (target) {
              target.title = title;
              target.content = content;
            }
            this.closeEditor();
          } else {
            this._swalErr(res.data && res.data.message);
          }
        } else {
          // create
          const res = await axios.post(
            expressService_dotnet + "ChatSavedReply/create",
            {
              shopId: this.shopId,
              title,
              content,
              staffShopId: this.staffShopId,
            },
            { headers: this._headers }
          );
          if (res.data && res.data.status === "success" && res.data.reply) {
            this.replies.push(res.data.reply);
            this.closeEditor();
          } else {
            this._swalErr(res.data && res.data.message);
          }
        }
      } catch (e) {
        this._swalErr(e.message);
      } finally {
        this.saving = false;
      }
    },
    async onDelete(r) {
      if (!window.confirm(`${this.use_language.delete} "${r.title}" ?`)) return;
      try {
        const res = await axios.post(
          expressService_dotnet + "ChatSavedReply/delete",
          { id: r.id, shopId: this.shopId },
          { headers: this._headers }
        );
        if (res.data && res.data.status === "success") {
          this.replies = this.replies.filter((x) => x.id !== r.id);
        }
      } catch (e) {
        console.warn("[SavedReply] delete error:", e.message || e);
      }
    },
    _swalErr(text) {
      this.$swal && this.$swal({ icon: "error", title: this.use_language.transaction_failed, text: text || "" });
    },
  },
};
</script>

<style scoped>
.saved-reply-card {
  max-height: 520px;
  display: flex;
  flex-direction: column;
}
.reply-list {
  flex: 1 1 auto;
  overflow-y: auto;
  max-height: 360px;
}
</style>